import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocalParticipant, useParticipants, useRoomContext, useTracks } from '@livekit/components-react'
import { Track } from 'livekit-client'

import { BackgroundPicker } from './BackgroundPicker'
import { ChatPanel } from './chat/ChatPanel'
import { useChat } from './chat/useChat'
import { ControlBar } from './ControlBar'
import { report } from './diagnostics'
import { FallbackDialog } from './FallbackDialog'
import { ConnectionPrompt, useConnectionPrompt } from './ConnectionPrompt'
import { moveClassToZoom } from './fallback'
import { studentDeviceReasonKey } from './fallbackGate'
import { useClassMoved, type ClassMoved } from './useClassMoved'
import { MediaReporter } from './MediaReporter'
import { useSpeakerChoice } from './audioOutput'
import { classifyDeviceError, getLastCamAccess, getLastMicAccess, needsHelp, type DeviceAccess, type MicAccess } from './deviceProbe'
import { MicHelpDialog } from './MicHelpDialog'
import { useMediaRecovery } from './mediaRecovery'
import { measuredRecoveryHintKey } from './publishHealth'
import { studentDeviceOk, studentHelpDevice, studentHelpTarget } from './studentHelp'
import { useReconnectProbe } from './useReconnectProbe'
import { ReactionLayer } from './ReactionLayer'
import { RoomHeader } from './RoomHeader'
import { Roster } from './Roster'
import { RoomToasts } from './RoomToasts'
import { ShareHelpDialog } from './ShareHelpDialog'
import { HelpDialog } from './HelpDialog'
import { detectPlatform } from './micHelp'
import { pipHelpSteps } from './pip'
import { SetupHub } from './help/SetupHub'
import { useSetupChecks } from './help/useSetupChecks'
import { Stage } from './Stage'
import { t } from './i18n'
import { ConfirmDialog } from './ConfirmDialog'
import { moderate, ModerationError, type ModerationAction } from './moderation'
import { moderationFailureKey, muteAllKey } from './moderationNotice'
import type { ModerationNote } from './RoomToasts'
import { isLiveScreenShare, SCREEN_SHARE_CAPTURE, SCREEN_SHARE_PUBLISH, type ShareFailure } from './screenShare'
import type { ShareHelpTopic } from './help/shareHelp'
import { useSurface } from './surface/useSurface'
import { useBackgroundEffect } from './useBackgroundEffect'
import { useClassroomSignals, isTeacher } from './useClassroomSignals'
import { useDataSaver } from './useDataSaver'
import { useGhostAutoLeave } from './useGhostAutoLeave'
import { useScreenSharePin } from './useScreenSharePin'
import { useTabCloseGuard } from './useTabCloseGuard'
import { useAudioUnlock } from './useAudioUnlock'
import { useNoiseFilter } from './useNoiseFilter'
import { usePictureInPicture } from './usePictureInPicture'
import { useDevices } from './useDevices'
import { micBannerVisible } from './micBanner'
import { PipStage } from './PipStage'
import type { ConnectData } from './types'

/**
 * How long a share has to look soundless before we say so.
 *
 * Long enough to outlast the gap between the share's video and audio
 * publications completing, short enough that a tutor who genuinely forgot the
 * sound hears about it before they have played much of the video.
 */
const SILENT_SHARE_SETTLE_MS = 1_500

/**
 * Everything inside the room, assembled in one place.
 *
 * This lives below <LiveKitRoom> rather than inside JoinPage because every hook
 * here needs the room context. Keeping it separate also means JoinPage stays a
 * state machine about *getting in*, and this file is about *being in*.
 */
export function Classroom({
  context,
  credential,
  intended,
  onLeave,
  onMoved,
  onEnded,
}: {
  context: ConnectData
  credential: { code?: string | null; reconnectToken?: string | null }
  /** Prejoin/rejoin choices — not LiveKit publication getters. */
  intended: { mic: boolean; cam: boolean }
  onLeave: () => void
  /** The class has fallen back to Zoom — hand the URL up and leave the room. */
  onMoved: (moved: ClassMoved) => void
  onEnded: () => void
}) {
  const room = useRoomContext()
  const { isCameraEnabled, isMicrophoneEnabled, localParticipant } = useLocalParticipant()
  // Everyone starts plain; a stored per-device choice always beats this default.
  const background = useBackgroundEffect(credential, 'off', context.current_user.meetingRole === 'Host')
  const signals = useClassroomSignals()

  /**
   * Re-acquire media when a device vanishes mid-lesson, without leaving the
   * session. A Ghost publishes nothing, so there is nothing to recover for them
   * and asking would only produce a permission prompt they must not see.
   */
  /**
   * A device fault worth interrupting the lesson for, or null.
   *
   * The walkthrough was built with a `tone: 'inroom'` mode from the start and
   * then only ever mounted on the prejoin — so every fault that happened AFTER
   * join (which is most of them: headsets get unplugged, Windows steals the
   * device, a parent changes a setting) reached the tutor's roster and never the
   * person who could actually fix it. `null` while nothing is wrong; dismissing
   * clears it and it can be raised again by the next event.
   */
  const [micFault, setMicFault] = useState<MicAccess | null>(null)
  const [camFault, setCamFault] = useState<MicAccess | null>(null)
  const [showMicHelp, setShowMicHelp] = useState(false)
  const [dismissedMicAccess, setDismissedMicAccess] = useState<DeviceAccess | null>(null)
  /** The tutor's last failed share attempt, until it is read or times out. */
  const [shareFailure, setShareFailure] = useState<ShareFailure | null>(null)
  const [shareHelp, setShareHelp] = useState<ShareHelpTopic | null>(null)
  const [showPipHelp, setShowPipHelp] = useState(false)
  const [setupHubOpen, setSetupHubOpen] = useState(false)
  const [setupHubFocus, setSetupHubFocus] = useState<'mic' | 'cam' | 'speaker' | 'share' | 'net' | undefined>()
  const setupHubButtonRef = useRef<HTMLButtonElement>(null)

  // Route playback to the speaker this person picked in the prejoin, on every
  // entry path (including reconnects, which never see the prejoin) and again
  // whenever the hardware changes underneath us.
  useSpeakerChoice()

  useReconnectProbe({
    reconnectToken: context.reconnect_token,
    onMoved,
    onEnded,
  })

  const recovery = useMediaRecovery({
    credential,
    enabled: context.current_user.meetingRole !== 'Ghost',
    intended,
    onAccess: signals.publishDeviceAccess,
    onFault: setMicFault,
  })
  const reportToggleFailure = useCallback(
    (kind: 'mic' | 'camera', access: DeviceAccess) => {
      if (kind === 'mic') setMicFault(access)
      else setCamFault(access)
      report(credential, {
        stage: 'permissions',
        outcome: 'error',
        permission: `${kind}=${access},reason=toggle`,
      })
      signals.publishDeviceAccess(kind === 'mic' ? access : null, kind === 'camera' ? access : null)
    },
    [credential, signals.publishDeviceAccess],
  )
  const enableMic = useCallback(async () => {
    try {
      await room.localParticipant.setMicrophoneEnabled(true)
      setMicFault(null)
      setDismissedMicAccess(null)
      signals.publishDeviceAccess('ok', null)
    } catch (error) {
      reportToggleFailure('mic', classifyDeviceError(error).access)
    }
  }, [room, signals.publishDeviceAccess, reportToggleFailure])
  const enableCamera = useCallback(async () => {
    try {
      await room.localParticipant.setCameraEnabled(true)
      setCamFault(null)
      signals.publishDeviceAccess(null, 'ok')
    } catch (error) {
      reportToggleFailure('camera', classifyDeviceError(error).access)
    }
  }, [room, signals.publishDeviceAccess, reportToggleFailure])
  const previousMicEnabled = useRef(isMicrophoneEnabled)
  const previousCamEnabled = useRef(isCameraEnabled)
  useEffect(() => {
    if (isMicrophoneEnabled && !previousMicEnabled.current && micFault) {
      setMicFault(null)
      setDismissedMicAccess(null)
      signals.publishDeviceAccess('ok', null)
    }
    previousMicEnabled.current = isMicrophoneEnabled
  }, [isMicrophoneEnabled, micFault, signals.publishDeviceAccess])
  useEffect(() => {
    if (isCameraEnabled && !previousCamEnabled.current && camFault) {
      setCamFault(null)
      signals.publishDeviceAccess(null, 'ok')
    }
    previousCamEnabled.current = isCameraEnabled
  }, [isCameraEnabled, camFault, signals.publishDeviceAccess])
  const micAccess = micFault ?? recovery.lastMicAccess ?? getLastMicAccess()
  const hasMicPublication = Boolean(localParticipant?.getTrackPublication(Track.Source.Microphone))
  const showMicBanner = micBannerVisible({
    isGhost: context.current_user.meetingRole === 'Ghost',
    micIntended: intended.mic,
    hasMicPublication,
    micAccess,
    dismissedAccess: dismissedMicAccess,
  })
  const retryMicHelp = async () => {
    if (!hasMicPublication) await enableMic()
    recovery.recover('manual')
  }
  const measuredHintKey = measuredRecoveryHintKey({
    label: recovery.lastLabel,
    mic: recovery.lastMicAccess,
    cam: recovery.lastCamAccess,
  })
  const measuredRecoveryHint = measuredHintKey ? t(measuredHintKey) : undefined
  // The picker must know when recovery owns capture so a headset change cannot
  // race two getUserMedia calls. It remains available to students and tutors.
  const devices = useDevices(recovery.running)

  // Hand the room the prejoin's microphone verdict, once, so the tutor's roster
  // can say WHY a student is silent instead of showing the same mute icon a child
  // who simply chose to mute would get. Advisory: if it never lands, the tutor
  // just sees today's behaviour.
  useEffect(() => {
    const mic = getLastMicAccess()
    const cam = getLastCamAccess()
    if (mic || cam) signals.publishDeviceAccess(mic, cam)
  }, [signals.publishDeviceAccess])
  // The server told us the role at exchange time (meeting_role_for → exactly
  // "Host" | "Participant" | "Ghost"); use it rather than the live LiveKit
  // identity, which is briefly empty on first render and flashed the student's
  // "waiting for your teacher" copy at the tutor.
  //
  // Only "Host" gets the teacher toolbar. A "Ghost" observer (Admin / Manager /
  // QualityMember) is hidden and holds a token with canPublishData:false and no
  // roomAdmin, so board / draw / celebrate / mute would every one silently fail
  // or 403 for them — a toolbar of dead buttons. They still SEE the board, ink
  // and celebrations, because receive is gated on the SENDER's identity, not on
  // this flag. (Earlier this was `!== 'Participant'`, which wrongly folded Ghost
  // in with Host.)
  const isHost = context.current_user.meetingRole === 'Host'
  // A hidden observer. Used below to end the observation once the class empties
  // out, so a ghost never keeps the room (and its recording) alive on its own.
  const isGhost = context.current_user.meetingRole === 'Ghost'
  // Host-only nudge: when the tutor's OWN connection goes Poor/Lost, offer the
  // in-place fix before they reach for Zoom. Escapable; never opens Zoom.
  const connectionPrompt = useConnectionPrompt(isHost, credential)
  // After isHost on purpose: the saver's small-class exemption needs the role.
  const saver = useDataSaver(isHost, credential)
  // A remote share must never be paused by adaptive stream: un-pausing a static
  // share can sit black waiting for a keyframe that a text-hinted encoder never
  // sends. Cameras keep the full adaptive behaviour — see the hook.
  useScreenSharePin(credential)
  const surface = useSurface(isHost)
  const teacher = isHost
  // LK-010: students get the opt-in RNNoise-only pill (never Krisp — Cloud
  // voice isolation is billed per minute; product decision is NO paid NC).
  // Tutors keep the provider-derived flow, which the current policy leaves
  // switched off entirely. MEET-NC-FIX-01: the session server URL lets the
  // hook derive Krisp capability; a student never reaches that branch.
  const noiseFilter = useNoiseFilter(isHost, credential, context.server_url)
  // The floating lesson window for whoever tabs away. Auto on Chromium
  // desktop (MediaSession), manual button elsewhere it can work at all.
  const pip = usePictureInPicture(isHost, credential)
  // Owned here rather than in ControlBar so the panel can be a child of
  // .classroom and overlay the stage. Rendered inside the bar it would inherit
  // that bar's backdrop-filter as its containing block — the same trap that put
  // the leave dialog off-screen.
  const [rosterOpen, setRosterOpen] = useState(false)
  const rosterButtonRef = useRef<HTMLButtonElement>(null)
  const [bgPickerOpen, setBgPickerOpen] = useState(false)
  const bgButtonRef = useRef<HTMLButtonElement>(null)
  const [chatOpen, setChatOpen] = useState(false)
  const chatButtonRef = useRef<HTMLButtonElement>(null)
  const chat = useChat({
    isHost,
    credential,
    myName: context.current_user.name ?? '',
  })
  const myIdentity = context.identity
  const toggleChat = () => {
    // The store side effect stays OUT of the setState updater: updaters must be
    // pure. React 18 StrictMode double-invokes them in dev (so the panel-open
    // emit fired twice per tap), and a future concurrent replay could turn the
    // pattern into a render-phase update crash.
    const next = !chatOpen
    chat.setPanelOpen(next)
    setChatOpen(next)
  }
  const participants = useParticipants()
  // One share at a time: while anyone presents, "ask to share" is pointless
  // and the ask item says so instead of sending a request nobody can act on.
  // isLiveScreenShare, not a bare source test: a moderated share stays published
  // and muted, and counting it kept `shareBusy` true forever — so after a tutor
  // stopped one student's share, nobody in the class could be asked to share again.
  const shareTracks = useTracks([Track.Source.ScreenShare], {
    onlySubscribed: false,
  }).filter(isLiveScreenShare)
  const shareBusy = shareTracks.length > 0
  const sharing = shareTracks.some((ref) => ref.participant.isLocal)

  /**
   * Whether MY share is carrying sound — the one thing about share audio the
   * app cannot infer for the tutor and they cannot see for themselves.
   *
   * Chrome hides audio behind a checkbox in its own picker, so a tutor who
   * misses it plays a video to a silent class and finds out when a child types
   * "no sound" into the chat. The publication is the honest answer: if the
   * checkbox was not ticked there is no ScreenShareAudio track at all.
   *
   * Derived from `useTracks` rather than read once after the share starts, so
   * this also covers the audio dying on its own — switching the shared tab from
   * Chrome's own sharing bar can end the audio track while the video carries on,
   * and the note appears for that too without any extra listener.
   *
   * Host-only (undefined for everyone else), the same convention `ncStatus`
   * uses: the remedy is an instruction about which browser surface to pick and
   * which option to tick, which is not something to put in front of an
   * eight-year-old who was asked to show their work.
   *
   * The honest cost of that choice: when a STUDENT is the one presenting, nobody
   * is told the share is silent — not the student, and not the tutor either. It
   * is the narrow case (students share only when asked, and almost all of them
   * are on Android phones, which cannot capture a screen at all), and the tutor
   * is talking to them at the time. If it turns out to matter, telling the tutor
   * about someone else's silent share is the extension to build.
   */
  const shareAudioTracks = useTracks([Track.Source.ScreenShareAudio], { onlySubscribed: false })
  const rawShareSound: 'on' | 'silent' | undefined = !isHost
    ? undefined
    : !shareTracks.some((ref) => ref.participant.isLocal)
      ? undefined
      : shareAudioTracks.some((ref) => ref.participant.isLocal)
        ? 'on'
        : 'silent'

  /**
   * The same verdict, but 'silent' has to HOLD before we believe it.
   *
   * The share's two tracks are published concurrently and resolve
   * independently, so there is a window — short on a desk, hundreds of
   * milliseconds on a bad uplink — where the video publication exists and the
   * audio one does not yet. Read raw, that window says 'silent': the tutor gets a
   * "this share has no sound" note that contradicts itself a moment later, and
   * the funnel gets a `share=silent` row for a share that carried sound
   * perfectly well, which would make tutors look careless in the one metric
   * meant to tell us whether they need better guidance.
   *
   * 'on' and "nobody is sharing" commit immediately — neither can be a
   * transient artefact of publishing, and there is nothing to gain by delaying
   * good news or a teardown.
   */
  const [shareSound, setShareSound] = useState<'on' | 'silent' | undefined>(undefined)
  useEffect(() => {
    if (rawShareSound !== 'silent') {
      setShareSound(rawShareSound)
      return
    }
    const timer = window.setTimeout(() => setShareSound('silent'), SILENT_SHARE_SETTLE_MS)
    return () => window.clearTimeout(timer)
  }, [rawShareSound])

  // Reported once per settled state change, on the free-form `permission` field
  // the funnel already carries (see diagnostics.ts). Without it "the class was
  // silent" and "the tutor never shared the sound" are the same row, and the
  // first cohort's support tickets would have to be read to tell them apart.
  useEffect(() => {
    if (!shareSound) return
    report(credential, {
      stage: 'permissions',
      outcome: 'ok',
      permission: shareSound === 'on' ? 'share=sound' : 'share=silent',
    })
  }, [shareSound, credential])

  const leave = () => {
    // Disconnect explicitly rather than just unmounting: an abandoned publisher
    // leaves a ghost participant in the room for the other side to stare at.
    void room?.disconnect().finally(onLeave)
  }
  const raiseShareFailure = useCallback((failure: ShareFailure) => {
    setShareFailure(failure)
    if (failure !== 'cancelled') report(credential, { stage: 'permissions', outcome: 'error', permission: `share=${failure}` })
  }, [credential])
  const retryShareHelp = async () => {
    await room.localParticipant.setScreenShareEnabled(true, SCREEN_SHARE_CAPTURE, SCREEN_SHARE_PUBLISH)
  }

  // Hidden observers do not appear to anyone (token `hidden: true`), so from a
  // ghost's own view every remote here is a visible classroom member. When that
  // count holds at zero long enough, the ghost leaves so it is not the last
  // thing keeping an empty class recording.
  const visibleRemoteCount = participants.filter((participant) => !participant.isLocal).length
  useGhostAutoLeave(isGhost, visibleRemoteCount, leave)

  // Closing the tab mid-class gets the browser's native confirm, mirroring the
  // in-app Leave dialog's intent — one keystroke must not end a lesson. Active
  // for the whole Classroom mount (every role, ghosts included: their exit is
  // also deliberate); gone the moment we are on a left/moved/error screen.
  // Also owns the pagehide disconnect (see the hook for why the SDK's own
  // page-leave hook is off).
  useTabCloseGuard(room)

  // The browser refusing to play remote audio is the quietest catastrophe this
  // app has: everything looks connected and the child hears nothing. Watched
  // for every participant, not just the ones who publish — an observer or a
  // mic-off child never gets the permission carve-out that hides this.
  const audio = useAudioUnlock(room)
  const checks = useSetupChecks({ open: setupHubOpen, recovery, shareFailure, boardOpen: surface.boardOpen, sharing })
  const anyBad = [checks.mic, checks.cam, checks.speaker, checks.share, checks.connection].some((verdict) => verdict.status === 'bad')
  const openSetupHub = useCallback((focus?: 'mic' | 'cam' | 'speaker' | 'share' | 'net') => {
    setSetupHubFocus(focus)
    setSetupHubOpen(true)
    report(credential, { stage: 'permissions', outcome: 'ok', permission: `help=open:${focus ?? 'menu'}` })
  }, [credential])
  const fixSetup = (action: 'fixMic' | 'fixCam' | 'unmute' | 'enableCam' | 'restartMedia' | 'unlockAudio' | 'pickSpeaker' | 'shareHelp' | 'closeBoard' | 'pip' | 'pipHelp') => {
    const row = action === 'fixMic' || action === 'unmute' ? 'mic' : action === 'fixCam' || action === 'enableCam' ? 'cam' : action === 'restartMedia' ? 'net' : action === 'unlockAudio' || action === 'pickSpeaker' ? 'speaker' : action === 'shareHelp' || action === 'closeBoard' ? 'share' : 'pip'
    const status = row === 'mic' ? checks.mic.status : row === 'cam' ? checks.cam.status : row === 'speaker' ? checks.speaker.status : row === 'share' ? checks.share.status : row === 'net' ? checks.connection.status : 'ok'
    report(credential, { stage: 'permissions', outcome: 'ok', permission: `help=fix:${row}:${status}` })
    if (action === 'fixMic') setShowMicHelp(true)
    else if (action === 'fixCam') setCamFault(recovery.lastCamAccess ?? 'blocked')
    else if (action === 'unmute') void room.localParticipant.setMicrophoneEnabled(true)
    else if (action === 'enableCam') void enableCamera()
    else if (action === 'restartMedia') recovery.recover('manual')
    else if (action === 'unlockAudio') audio.unlock()
    else if (action === 'shareHelp') setShareHelp(shareFailure ?? 'failed')
    else if (action === 'closeBoard') surface.closeBoard()
    else if (action === 'pip') pip.toggle()
    else if (action === 'pipHelp') setShowPipHelp(true)
  }

  // Everyone in the room listens for the server's "we have moved to Zoom"
  // announce — students and tutor alike. It arrives a moment before the room is
  // deleted under us, which is the whole point of it. `isHost` is passed so the
  // client can refuse a host link it should never have been sent.
  useClassMoved(isHost, onMoved)

  const [fallbackOpen, setFallbackOpen] = useState(false)
  const [studentAsk, setStudentAsk] = useState<{ identity: string; device: 'mic' | 'camera'; sentAt: number } | null>(null)
  const [fallbackBusy, setFallbackBusy] = useState(false)
  const [fallbackError, setFallbackError] = useState<string | null>(null)

  const confirmFallback = useCallback(
    async (reason: string) => {
      setFallbackBusy(true)
      setFallbackError(null)
      try {
        const result = await moveClassToZoom(credential, reason)
        // start_url when we have it, so the tutor lands as the Zoom host rather
        // than waiting in their own meeting.
        const url = result.start_url ?? result.meeting_url
        if (!url) {
          // The class HAS moved at this point — the switch succeeded and only
          // the link is missing. Say so rather than implying it failed.
          setFallbackError(t('fallback.movedNoLink'))
          setFallbackBusy(false)
          return
        }
        // Not run through isZoomUrl: unlike the data-channel announce, this came
        // from our own API over HTTPS, and rejecting a legitimate vanity domain
        // here would strand the tutor for no security gain.
        onMoved({ url, host: result.start_url != null })
      } catch (error) {
        setFallbackError(error instanceof Error ? error.message : t('fallback.failed'))
        setFallbackBusy(false)
      }
    },
    [credential, onMoved],
  )

  // Muting someone else needs room-admin authority, so it goes through our API
  // rather than the client SDK. The server re-derives from this credential that
  // the caller really is this class's teacher — the identity below is only a
  // target, never a claim about who is asking.
  const moderateParticipant = useCallback(
    async (identity: string, action: ModerationAction) => {
      await moderate(credential, identity, action)
    },
    [credential],
  )

  // Remove and mute-all report through a toast rather than the tile menu: the
  // removed student's tile (and its menu) is gone by the time the answer lands,
  // and mute-all has no tile at all.
  const [moderationNote, setModerationNote] = useState<ModerationNote | null>(null)
  const noteModerationFailure = useCallback((error: unknown) => {
    const key = moderationFailureKey(error instanceof ModerationError ? error.status : undefined)
    // Anything unclassified keeps the server's own sentence, as the tile menu does.
    const text = key === 'moderate.failed' && error instanceof Error && error.message ? error.message : t(key)
    setModerationNote({ text, error: true, at: Date.now() })
  }, [])

  /** Who the teacher asked to remove, until they confirm or cancel. */
  const [removing, setRemoving] = useState<
    { identity: string; name: string; returnFocus: HTMLElement | null } | null
  >(null)
  const requestRemove = useCallback((identity: string, name: string) => {
    setRemoving({ identity, name, returnFocus: document.activeElement as HTMLElement | null })
  }, [])
  const confirmRemove = async () => {
    if (!removing) return
    const target = removing
    setRemoving(null)
    try {
      await moderate(credential, target.identity, 'remove')
      setModerationNote({
        text: t('moderate.removed').replace('{name}', isolate(target.name)),
        error: false,
        at: Date.now(),
      })
    } catch (error) {
      noteModerationFailure(error)
      target.returnFocus?.focus()
    }
  }

  const muteAllStudents = useCallback(async () => {
    try {
      const result = await moderate(credential, null, 'mute_all')
      setModerationNote({ text: t(muteAllKey(result?.muted)), error: false, at: Date.now() })
    } catch (error) {
      noteModerationFailure(error)
    }
  }, [credential, noteModerationFailure])

  /**
   * The one-line truth about a student's own hardware, if we have it — what the
   * fallback dialog leads with so a tutor is not talked into moving a class for a
   * fault the destination shares. Only student-side states count: a tutor whose
   * own media is broken may genuinely be better off on Zoom.
   *
   * ORDER AND SCOPE both matter, and getting them wrong would ship a worse
   * recommendation than saying nothing:
   *
   * - Microphone faults outrank a missing camera. A silent child is the thing that
   *   actually ends lessons; a missing webcam is usually benign. Checking the
   *   camera first meant "student A has no camera" could mask "student B cannot be
   *   heard" and send the tutor the wrong message entirely.
   * - A missing camera only routes in a ONE-TO-ONE class. Desktops without webcams
   *   are common, so in a group of five a single camera-less child must not flip
   *   the whole dialog to "keep teaching here" when the real pain might be
   *   somebody else's audio.
   * - A BLOCKED camera routes in any class size: Zoom will not unblock it either.
   */
  const studentDeviceReason = (() => {
    const students = (identity: string) => !isTeacher(identity)
    const key = studentDeviceReasonKey({
      micFaults: Object.entries(signals.micAccess).filter(([id]) => students(id)).map(([, a]) => a),
      camFaults: Object.entries(signals.camAccess).filter(([id]) => students(id)).map(([, a]) => a),
      soloClass: context.session.type === '1:1',
    })
    return key ? t(key) : null
  })()

  const helpTarget = studentHelpTarget({ micAccess: signals.micAccess, camAccess: signals.camAccess, isTeacher })
  const helpDevice = helpTarget ? studentHelpDevice(helpTarget, signals.micAccess) : null
  // A new student's fault needs its own ask; losing stale attributes is no answer.
  const applicableAsk = studentAsk && (!helpTarget || (studentAsk.identity === helpTarget && studentAsk.device === helpDevice))
    ? studentAsk : null
  const studentAnswered = !!applicableAsk && studentDeviceOk(applicableAsk.identity, applicableAsk.device, signals.micAccess, signals.camAccess)

  return (
    <>
      <RoomHeader
        context={context}
        anyBad={teacher && anyBad}
        onOpenSetupHub={() => openSetupHub()}
        // Host-only, same rule as before the move: a student's screen carries
        // no trace of an action they cannot take.
        onFallback={teacher ? () => {
          setFallbackError(null)
          setFallbackOpen(true)
        } : undefined}
      />
      <MediaReporter credential={credential} isHost={isHost} />

      <div className="classroom">
        <Stage
          hands={signals.hands}
          reactions={signals.floating}
          isTeacherView={teacher}
          // Passed only for a teacher, so a student's tiles carry no moderation
          // affordance at all rather than one that fails on the server.
          onModerate={teacher ? moderateParticipant : undefined}
          onAsk={teacher ? signals.askToUnmute : undefined}
          onRemove={teacher ? requestRemove : undefined}
          surface={surface}
          saverLevel={saver.level}
          saverKeepStudents={saver.keepStudents}
          weakPeers={saver.weakPeers}
          onAskShare={teacher ? signals.askToShare : undefined}
          canShare={signals.canShare}
          micAccess={signals.micAccess}
          sharePending={signals.sharePending}
          shareBusy={shareBusy}
          credential={credential}
          selfViewMirrored={background.mode === 'off'}
        />
        <ReactionLayer celebrating={signals.celebrating} />

        {/* Floating-window content: a portal in document mode, an offscreen
            donor <video> in element mode, nothing otherwise. */}
        <PipStage
          pip={pip}
          saverLevel={saver.level}
          saverKeepStudents={saver.keepStudents}
          isHost={teacher}
          handCount={signals.handQueue.length}
          chat={chat}
          onOpenRoster={() => setRosterOpen(true)}
          onOpenChat={toggleChat}
          onDeviceToggleFailed={reportToggleFailure}
          micFault={micFault}
          credential={credential}
        />

        {/* Reconnect banner, blur notice and unmute prompt all live here, floating
            over the stage. As flex rows they reflowed the video every time one
            appeared. */}
        <RoomToasts
          audioBlocked={audio.blocked}
          onUnlockAudio={audio.unlock}
          recoveringMedia={recovery.running}
          shareFailure={shareFailure}
          onDismissShareFailure={() => setShareFailure(null)}
          onShareHelp={setShareHelp}
          shareSound={shareSound}
          shareStoppedAt={signals.shareStoppedAt}
          backgroundStatus={background.status}
          unmuteRequest={signals.unmuteRequest}
          onDismissUnmute={signals.dismissUnmuteRequest}
          saverLevel={saver.noticeLevel}
          saverManual={saver.manual}
          saverNoteDismissed={saver.noteDismissed}
          onSaverOff={saver.toggleManual}
          onSaverResume={saver.resumeVideos}
          onSaverDismiss={saver.dismissNote}
          ncStatus={noiseFilter.status}
          shareRequest={signals.shareRequest}
          onAnswerShare={signals.answerShare}
          onDismissShare={signals.dismissShareRequest}
          onShareAcceptStart={signals.holdShareTimeout}
          deviceNotice={devices.notice}
          micBanner={showMicBanner}
          isHost={isHost}
          onFixMic={() => setShowMicHelp(true)}
          onDismissMicBanner={() => setDismissedMicAccess(micAccess)}
          pip={pip}
          onFloatNow={pip.toggle}
          credential={credential}
          moderationNote={moderationNote}
          onDismissModerationNote={() => setModerationNote(null)}
        />

        {/* F3 — gate the mount instead of mounting-and-early-returning. With
            the panel closed, the two room-wide subscriptions (useParticipants
            + a 3-source useTracks) that drove its list no longer run, and the
            panel only re-subscribes when the user opens it. The focus-restore
            ref is already wired so unmount does not strand a keyboard or
            screen-reader user — see Roster.tsx for the cleanup. */}
        {rosterOpen ? (
          <Roster
            onClose={() => setRosterOpen(false)}
            hands={signals.hands}
            onModerate={teacher ? moderateParticipant : undefined}
            onAsk={teacher ? signals.askToUnmute : undefined}
            onAskShare={teacher ? signals.askToShare : undefined}
            canShare={signals.canShare}
            micAccess={signals.micAccess}
            sharePending={signals.sharePending}
            shareBusy={shareBusy}
            returnFocusTo={rosterButtonRef}
            onRemove={teacher ? requestRemove : undefined}
            onMuteAll={teacher ? muteAllStudents : undefined}
          />
        ) : null}

        {removing ? (
          <ConfirmDialog
            icon="🚪"
            title={t('moderate.removeTitle').replace('{name}', isolate(removing.name))}
            body={t('moderate.removeBody')}
            confirmLabel={t('moderate.removeYes')}
            cancelLabel={t('moderate.removeKeep')}
            onConfirm={() => void confirmRemove()}
            onCancel={() => {
              removing.returnFocus?.focus()
              setRemoving(null)
            }}
          />
        ) : null}

        {setupHubOpen ? <SetupHub checks={checks} devices={devices} pip={pip} credential={credential} focus={setupHubFocus} onClose={() => setSetupHubOpen(false)} returnFocusTo={setupHubButtonRef} onAction={fixSetup} /> : null}

        <ChatPanel
          open={chatOpen}
          onClose={() => {
            chat.setPanelOpen(false)
            setChatOpen(false)
          }}
          chat={chat}
          isHost={teacher}
          // Ghost observers are hidden and cannot publish data — they read the
          // class chat, they never appear in it.
          canSend={context.current_user.meetingRole !== 'Ghost'}
          myIdentity={myIdentity}
          returnFocusTo={chatButtonRef}
        />

        <BackgroundPicker
          open={bgPickerOpen}
          mode={background.mode}
          busy={background.busy}
          degraded={background.degraded}
          onSelect={background.select}
          onPrefetch={background.prefetch}
          onClose={() => setBgPickerOpen(false)}
          returnFocusTo={bgButtonRef}
        />
      </div>

      <ControlBar
        background={background}
        saverManual={saver.manual}
        saverAuto={saver.autoEngaged}
        // A tap while AUTO is engaged re-opens the explanation instead of
        // silently latching manual — a child tapping the lit button to "fix
        // the video" must get the story, not a persistent setting they never
        // chose. (GROK-SAVER HIGH-2.)
        onToggleSaver={() => {
          if (saver.autoEngaged && !saver.manual) saver.showNote()
          else saver.toggleManual()
        }}
        onReaction={signals.sendReaction}
        onCelebrate={signals.celebrate}
        noiseFilter={noiseFilter}
        pip={pip}
        handRaised={signals.myHandRaised}
        onToggleHand={signals.toggleHand}
        handQueue={signals.handQueue}
        onLeave={leave}
        // Take-over stops the other share through the server (room admin), the
        // same authority that powers the tile menu's stop-share.
        onStopShare={teacher ? (identity) => moderateParticipant(identity, 'stop_share') : undefined}
        onShareFailure={raiseShareFailure}
        surface={surface}
        isHost={teacher}
        bgPickerOpen={bgPickerOpen}
        onToggleBgPicker={() => setBgPickerOpen((open) => !open)}
        bgButtonRef={bgButtonRef}
        chatOpen={chatOpen}
        onToggleChat={toggleChat}
        chat={chat}
        chatButtonRef={chatButtonRef}
        rosterOpen={rosterOpen}
        onToggleRoster={() => setRosterOpen((open) => !open)}
        participantCount={participants.length}
        rosterButtonRef={rosterButtonRef}
        devices={devices}
        onDeviceToggleFailed={reportToggleFailure}
        onOpenSetupHub={() => openSetupHub()}
        setupHubOpen={setupHubOpen}
        setupHubButtonRef={setupHubButtonRef}
      />


      {/* Mid-lesson device help. Escapable by design (`tone="inroom"`): a child
          must always be able to stay in a lesson they have paid for with a
          broken microphone, so this informs and offers a retry — it never
          blocks. Retry runs the same recovery path the automatic triggers use,
          so "I flipped the switch, now fix it" is one tap. */}
      {(micFault && needsHelp(micFault)) || signals.micHelpRequest?.device === 'mic' || showMicHelp ? (
        <MicHelpDialog
          access={micFault ?? micAccess ?? 'silent'}
          tone="inroom"
          retrying={recovery.running}
          onRetry={retryMicHelp}
          onDismiss={() => {
            setMicFault(null)
            setShowMicHelp(false)
            signals.dismissMicHelpRequest()
          }}
        />
      ) : null}

      {camFault || signals.micHelpRequest?.device === 'camera' ? (
        <MicHelpDialog
          device="camera"
          access={camFault ?? recovery.lastCamAccess ?? getLastCamAccess() ?? 'unknown'}
          tone="inroom"
          retrying={recovery.running}
          onRetry={enableCamera}
          onDismiss={() => {
            setCamFault(null)
            signals.dismissMicHelpRequest()
          }}
        />
      ) : null}

      {shareHelp ? <ShareHelpDialog topic={shareHelp} onRetry={retryShareHelp} onDismiss={() => setShareHelp(null)} /> : null}
      {/* Auto-PiP looked blocked (tab hidden twice, window never floated): coach
          the per-site Chrome setting. Retry = float manually, a real gesture. */}
      {showPipHelp ? (
        <HelpDialog
          title={t('pip.autoBlockedTitle')}
          steps={pipHelpSteps(detectPlatform().platform)}
          onRetry={() => { setShowPipHelp(false); pip.toggle() }}
          retryLabel={t('pip.enter')}
          onDismiss={() => setShowPipHelp(false)}
          tone="inroom"
        />
      ) : null}

      {fallbackOpen ? (
        <FallbackDialog
          busy={fallbackBusy}
          error={fallbackError}
          studentDeviceReason={studentDeviceReason}
          measuredHint={measuredRecoveryHint}
          // Fix before Zoom: the primary action recovers this tab's media in place
          // (same path as the automatic triggers), or gives the student time
          // to try the walkthrough on their affected device.
          onFix={() => {
            if (studentDeviceReason && helpTarget && helpDevice) {
              signals.askStudentMicHelp(helpTarget, helpDevice)
              setStudentAsk({ identity: helpTarget, device: helpDevice, sentAt: Date.now() })
            }
            else recovery.recover('manual')
          }}
          recovering={recovery.running}
          recoveryAttempts={recovery.attempts}
          recoveryCompleted={recovery.lastLabel !== null && !recovery.running}
          canAskStudent={!!helpTarget}
          askSentAt={applicableAsk?.sentAt}
          studentAnswered={studentAnswered}
          sessionStartAt={context.session.start_at}
          onConfirm={(reason) => void confirmFallback(reason)}
          onCancel={() => {
            // Never mid-request: the class may already be moving, and closing
            // the dialog would hide the only progress the tutor can see.
            if (fallbackBusy) return
            setFallbackOpen(false)
          }}
        />
      ) : null}

      {/* Weak-connection nudge. Auto-shown when the tutor's own quality drops,
          escapable, and pointed at the in-place fix — not Zoom. */}
      {connectionPrompt.show ? (
        <ConnectionPrompt
          recovering={recovery.running}
          onFix={() => recovery.recover('manual')}
          onOpenSetupHub={() => openSetupHub('net')}
          onDismiss={connectionPrompt.dismiss}
        />
      ) : null}
    </>
  )
}

/**
 * A name inside a translated sentence, isolated so an Arabic name in English
 * copy (or the reverse) cannot reorder the words around it.
 */
function isolate(name: string): string {
  return `\u2068${name}\u2069`
}
